/**
 * Certificate Store - Zustand
 *
 * Centralized state management for certificates (compliance standards)
 *
 * Features:
 * - CRUD operations (Create, Read, Update, Delete)
 * - Real-time search and filtering
 * - Pagination support
 * - Sorting capabilities
 * - Loading and error states
 * - Optimistic UI updates
 * - Toast notifications
 *
 * Backend API: /api/certificates
 *
 * @module useCertificateStore
 */

import { create } from 'zustand'
import { certificateService } from '@services/certificateService'
import toast from 'react-hot-toast'

/**
 * Certificate Store
 *
 * State Management for Certificates
 */
export const useCertificateStore = create((set, get) => ({
  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * List of all certificates
   * @type {Array<Object>}
   */
  certificates: [],

  /**
   * Currently selected certificate for viewing/editing
   * @type {Object|null}
   */
  selectedCertificate: null,

  /**
   * Loading state for async operations
   * @type {boolean}
   */
  loading: false,

  /**
   * Error message if operation fails
   * @type {string|null}
   */
  error: null,

  /**
   * Filter and search parameters
   * @type {Object}
   */
  filters: {
    search: '',              // Search by title/description
    status: 'all',           // all, active, archived
    sortBy: 'title',         // Field to sort by
    sortOrder: 'asc',        // asc or desc
  },

  /**
   * Pagination configuration
   * @type {Object}
   */
  pagination: {
    page: 1,                 // Current page
    limit: 25,               // Items per page
    total: 0,                // Total number of items
    totalPages: 0,           // Total number of pages
  },

  /**
   * Statistics about certificates
   * @type {Object}
   */
  stats: {
    total: 0,
    active: 0,
    archived: 0,
    inUse: 0,
  },

  // ============================================================================
  // ACTIONS - READ OPERATIONS
  // ============================================================================

  /**
   * Fetch all certificates with current filters
   *
   * @returns {Promise<void>}
   */
  fetchCertificates: async () => {
    set({ loading: true, error: null })

    try {
      const { filters, pagination } = get()

      // Build query parameters
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      }

      if (filters.search) {
        params.search = filters.search
      }

      if (filters.status !== 'all') {
        params.status = filters.status
      }

      params.sort = `${filters.sortBy}:${filters.sortOrder}`

      // Make API request via service
      const response = await certificateService.getAll(params)

      // Backend returns { status, message, data: [...], meta: {...} }
      const certificates = response.data || []
      const meta = response.meta || {}

      // Update state with response data
      set({
        certificates: certificates,
        pagination: {
          ...pagination,
          page: meta.page || pagination.page,
          total: meta.total || 0,
          totalPages: meta.total_pages || Math.ceil((meta.total || 0) / pagination.limit),
        },
        loading: false,
      })

      // Update statistics
      get().calculateStats()

    } catch (error) {
      console.error('Failed to fetch certificates:', error)
      set({
        error: error.message || 'Failed to load certificates',
        loading: false,
      })
      toast.error('Failed to load certificates')
    }
  },

  /**
   * Fetch single certificate by ID
   *
   * @param {number} id - Certificate ID
   * @returns {Promise<Object>} Certificate data
   */
  fetchCertificateById: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await certificateService.getById(id)
      const certificate = response.data || response

      set({
        selectedCertificate: certificate,
        loading: false,
      })

      return certificate

    } catch (error) {
      console.error('Failed to fetch certificate:', error)
      set({
        error: error.message || 'Certificate not found',
        loading: false,
      })
      toast.error('Certificate not found')
      throw error
    }
  },

  /**
   * Fetch certificate by slug
   *
   * @param {string} slug - Certificate slug
   * @returns {Promise<Object>} Certificate data
   */
  fetchCertificateBySlug: async (slug) => {
    try {
      const response = await certificateService.getBySlug(slug)
      const certificate = response.data || response
      set({ selectedCertificate: certificate })
      return certificate
    } catch (error) {
      console.error('Failed to fetch certificate:', error)
      toast.error('Certificate not found')
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - CREATE OPERATIONS
  // ============================================================================

  /**
   * Create new certificate
   *
   * @param {Object} data - Certificate data
   * @param {string} data.title - Certificate title
   * @param {string} data.slug - URL-friendly slug
   * @param {string} data.description - Certificate description
   * @param {string} data.icon - Icon identifier
   * @param {string} data.status - Status (active/archived)
   * @returns {Promise<Object>} Created certificate
   */
  createCertificate: async (data) => {
    set({ loading: true, error: null })

    try {
      const response = await certificateService.create(data)
      const newCertificate = response.data || response

      // Optimistic UI update - add to beginning of list
      set((state) => ({
        certificates: [newCertificate, ...state.certificates],
        pagination: {
          ...state.pagination,
          total: state.pagination.total + 1,
        },
        loading: false,
      }))

      toast.success('Certificate created successfully!')

      // Update statistics
      get().calculateStats()

      return newCertificate

    } catch (error) {
      console.error('Failed to create certificate:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to create certificate'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - UPDATE OPERATIONS
  // ============================================================================

  /**
   * Update existing certificate
   *
   * @param {number} id - Certificate ID
   * @param {Object} data - Updated certificate data
   * @returns {Promise<Object>} Updated certificate
   */
  updateCertificate: async (id, data) => {
    set({ loading: true, error: null })

    try {
      const response = await certificateService.update(id, data)
      const updatedCertificate = response.data || response

      // Optimistic UI update - replace in list
      set((state) => ({
        certificates: state.certificates.map((cert) =>
          cert.id == id ? updatedCertificate : cert
        ),
        selectedCertificate: state.selectedCertificate?.id == id
          ? updatedCertificate
          : state.selectedCertificate,
        loading: false,
      }))

      toast.success('Certificate updated successfully!')

      // Update statistics
      get().calculateStats()

      return updatedCertificate

    } catch (error) {
      console.error('Failed to update certificate:', error)

      // Extract detailed error message
      let errorMessage = 'Failed to update certificate'

      if (error.response?.data) {
        const responseData = error.response.data

        if (responseData.messages) {
          if (typeof responseData.messages === 'object') {
            const errors = Object.values(responseData.messages).filter(msg => typeof msg === 'string')
            if (errors.length > 0) {
              errorMessage = errors.join(', ')
            }
          } else if (typeof responseData.messages === 'string') {
            errorMessage = responseData.messages
          }
        } else if (responseData.message) {
          errorMessage = responseData.message
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - DELETE OPERATIONS
  // ============================================================================

  /**
   * Delete (archive) certificate
   * Soft delete - preserves data but marks as deleted
   *
   * @param {number} id - Certificate ID
   * @returns {Promise<void>}
   */
  deleteCertificate: async (id) => {
    set({ loading: true, error: null })

    try {
      await certificateService.delete(id)

      // Optimistic UI update - mark as archived and set deleted_at
      set((state) => ({
        certificates: state.certificates.map((cert) =>
          cert.id == id ? { ...cert, status: 'archived', deleted_at: new Date().toISOString() } : cert
        ),
        selectedCertificate: state.selectedCertificate?.id == id ? null : state.selectedCertificate,
        loading: false,
      }))

      toast.success('Certificate archived successfully!')

      // Update statistics
      get().calculateStats()

      // Refresh to apply filters (archived certificates may be hidden)
      setTimeout(() => get().fetchCertificates(), 100)

    } catch (error) {
      console.error('Failed to delete certificate:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete certificate'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Restore archived certificate
   *
   * @param {number} id - Certificate ID
   * @returns {Promise<void>}
   */
  restoreCertificate: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await certificateService.restore(id)
      const restored = response.data || response

      // Optimistic UI update - use actual restored data from backend
      set((state) => ({
        certificates: state.certificates.map((cert) =>
          cert.id == id ? restored : cert
        ),
        selectedCertificate: state.selectedCertificate?.id == id ? null : state.selectedCertificate,
        loading: false,
      }))

      toast.success('Certificate restored successfully!')

      // Update statistics
      get().calculateStats()

      // Refresh to apply filters (will remove from list if viewing archived only)
      setTimeout(() => get().fetchCertificates(), 100)

    } catch (error) {
      console.error('Failed to restore certificate:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to restore certificate'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - FILTER & SEARCH
  // ============================================================================

  /**
   * Update search/filter parameters and refresh data
   *
   * @param {Object} newFilters - New filter values
   */
  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 1 }, // Reset to page 1 on filter change
    }))

    // Fetch with new filters
    get().fetchCertificates()
  },

  /**
   * Update search term
   *
   * @param {string} searchTerm - Search query
   */
  setSearch: (searchTerm) => {
    get().setFilters({ search: searchTerm })
  },

  /**
   * Update status filter
   *
   * @param {string} status - Status filter (all, active, archived)
   */
  setStatusFilter: (status) => {
    get().setFilters({ status })
  },

  /**
   * Update sorting
   *
   * @param {string} sortBy - Field to sort by
   * @param {string} sortOrder - Sort order (asc/desc)
   */
  setSorting: (sortBy, sortOrder = 'asc') => {
    get().setFilters({ sortBy, sortOrder })
  },

  /**
   * Clear all filters
   */
  clearFilters: () => {
    set({
      filters: {
        search: '',
        status: 'all',
        sortBy: 'title',
        sortOrder: 'asc',
      },
      pagination: {
        ...get().pagination,
        page: 1,
      },
    })

    get().fetchCertificates()
  },

  // ============================================================================
  // ACTIONS - PAGINATION
  // ============================================================================

  /**
   * Go to specific page
   *
   * @param {number} page - Page number
   */
  setPage: (page) => {
    set((state) => ({
      pagination: { ...state.pagination, page },
    }))

    get().fetchCertificates()
  },

  /**
   * Change items per page
   *
   * @param {number} limit - Items per page
   */
  setPageLimit: (limit) => {
    set((state) => ({
      pagination: { ...state.pagination, limit, page: 1 },
    }))

    get().fetchCertificates()
  },

  /**
   * Go to next page
   */
  nextPage: () => {
    const { pagination } = get()
    if (pagination.page < pagination.totalPages) {
      get().setPage(pagination.page + 1)
    }
  },

  /**
   * Go to previous page
   */
  prevPage: () => {
    const { pagination } = get()
    if (pagination.page > 1) {
      get().setPage(pagination.page - 1)
    }
  },

  // ============================================================================
  // ACTIONS - UTILITIES
  // ============================================================================

  /**
   * Calculate statistics from current certificates
   */
  calculateStats: () => {
    const { certificates } = get()

    const stats = {
      total: certificates.length,
      active: certificates.filter((cert) => cert.status === 'active').length,
      archived: certificates.filter((cert) => cert.status === 'archived').length,
      inUse: certificates.filter((cert) => cert.packages_count > 0).length,
    }

    set({ stats })
  },

  /**
   * Select certificate for viewing/editing
   *
   * @param {Object|null} certificate - Certificate to select
   */
  selectCertificate: (certificate) => {
    set({ selectedCertificate: certificate })
  },

  /**
   * Clear selected certificate
   */
  clearSelection: () => {
    set({ selectedCertificate: null })
  },

  /**
   * Reset entire store to initial state
   */
  reset: () => {
    set({
      certificates: [],
      selectedCertificate: null,
      loading: false,
      error: null,
      filters: {
        search: '',
        status: 'all',
        sortBy: 'title',
        sortOrder: 'asc',
      },
      pagination: {
        page: 1,
        limit: 25,
        total: 0,
        totalPages: 0,
      },
      stats: {
        total: 0,
        active: 0,
        archived: 0,
        inUse: 0,
      },
    })
  },
}))

export default useCertificateStore
